import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { campaignAdProductLabel, type CampaignAdProduct } from '@/types/spCampaigns';
import { CreateSpCampaignForm } from '@/components/campaign-create/CreateSpCampaignForm';
import { CreateSbCampaignForm } from '@/components/campaign-create/CreateSbCampaignForm';
import { CreateSdCampaignForm } from '@/components/campaign-create/CreateSdCampaignForm';

function profileDashboardPath(profileId: string): string {
  return `/accounts/${encodeURIComponent(profileId)}/campaigns/dashboard`;
}

function parseAdProductParam(raw: string | undefined): CampaignAdProduct | null {
  const v = raw?.toUpperCase();
  if (v === 'SP' || v === 'SB' || v === 'SD') return v;
  return null;
}

export function CreateCampaignPage() {
  const { profileId, adProduct } = useParams<{ profileId: string; adProduct: string }>();
  const navigate = useNavigate();
  const product = parseAdProductParam(adProduct);

  useEffect(() => {
    if (!profileId || !product) {
      if (profileId) navigate(profileDashboardPath(profileId), { replace: true });
      else navigate('/accounts', { replace: true });
    }
  }, [profileId, product, navigate]);

  if (!profileId || !product) return null;

  const goBack = () => navigate(profileDashboardPath(profileId));
  const onSuccess = () => navigate(profileDashboardPath(profileId));

  return (
    <div className={product === 'SP' ? 'max-w-6xl mx-auto p-6' : 'max-w-2xl mx-auto p-6'}>
      <Link
        to={profileDashboardPath(profileId)}
        className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to dashboard
      </Link>
      <h1 className="text-xl font-semibold mb-6">New {campaignAdProductLabel(product)} campaign</h1>
      <div className="bg-surface border border-border rounded-xl shadow-sm p-6">
        {product === 'SP' && (
          <CreateSpCampaignForm profileId={profileId} onSuccess={onSuccess} onCancel={goBack} />
        )}
        {product === 'SB' && (
          <CreateSbCampaignForm profileId={profileId} onSuccess={onSuccess} onCancel={goBack} />
        )}
        {product === 'SD' && (
          <CreateSdCampaignForm profileId={profileId} onSuccess={onSuccess} onCancel={goBack} />
        )}
      </div>
    </div>
  );
}
